import {
  GlobalBlockTypes,
  ManagedBuildReference,
  PaymentSettings,
  TBuild,
  TypeOfBuild,
} from '@/types';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import * as functions from '@/services/Builds/build.services';

interface BuildSettings {
  name?: string;
  allowCashManagement?: boolean;
  requirePin?: boolean;
  previewImage?: string;
  description?: string;
  tag?: string;
}

export const buildApiService = createApi({
  reducerPath: 'api',
  tagTypes: ['Builds'],
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    fetchBuildsByCompany: builder.query<
      (TBuild | TBuild)[] | undefined,
      { companyId: string; buildType?: TypeOfBuild; isPublished?: boolean }
    >({
      async queryFn({ companyId, buildType, isPublished }) {
        const data = await functions.fetchBuildsByCompany({
          companyId,
          buildType,
          isPublished,
        });
        return { data };
      },
    }),
    findOneBuildByCompany: builder.query<TBuild | TBuild | undefined, string>({
      async queryFn(companyId) {
        const data = await functions.findOneBuildByCompany(companyId);
        return { data };
      },
    }),
    fetchManagedReferencesByCompany: builder.query<
      ManagedBuildReference[],
      { companyId: string; gbType?: GlobalBlockTypes }
    >({
      async queryFn(config) {
        const data = await functions.fetchManagedReferencesByCompany(
          config.companyId,
          config.gbType,
        );
        return { data };
      },
      providesTags: () => [{ type: 'Builds', id: 'fetchManagedReferencesByCompany' }],
    }),

    fetchManagedBuildsByCompany: builder.query<
      (TBuild | TBuild)[] | undefined,
      { companyId: string; gbType?: GlobalBlockTypes }
    >({
      async queryFn({ companyId, gbType }) {
        const data = await functions.fetchManagedBuildsByCompany({
          companyId,
          gbType,
        });
        return { data };
      },
    }),
    fetchPublicManagedBuilds: builder.query<TBuild[], void>({
      async queryFn() {
        const data = await functions.fetchPublicManagedBuilds();
        return { data };
      },
    }),
    insertOneBuild: builder.mutation<
      void,
      {
        name: string;
        allowCashManagement: boolean;
        requirePin: boolean;
        companyId: string;
        accountId: string;
      }
    >({
      async queryFn(newBuild) {
        const data = await functions.insertOneBuild(newBuild);
        return { data };
      },
    }),
    deleteBuild: builder.mutation<void, string>({
      async queryFn(buildId) {
        const data = await functions.deleteBuild(buildId);
        return { data };
      },
    }),
    uninstallManagedBuild: builder.mutation<void, { buildId: string; companyId: string }>({
      async queryFn(details) {
        const data = await functions.uninstallManagedBuild(details);
        return { data };
      },
    }),
    duplicateBuild: builder.mutation<void, string>({
      async queryFn(buildId) {
        const data = await functions.duplicateBuild(buildId);
        return { data };
      },
    }),
    updateBuildSettings: builder.mutation<void, { buildId: string; updates: BuildSettings }>({
      async queryFn({ buildId, updates }) {
        const data = await functions.updateBuildSettings(buildId, updates);
        return { data };
      },
    }),
    installManagedBuild: builder.mutation<
      void,
      {
        buildId: string;
        companyId: string;
        enableCashManagement: boolean;
        requireEmployeePin: boolean;
        paymentSettings: PaymentSettings;
      }
    >({
      async queryFn(details) {
        const data = await functions.installManagedBuild(details);
        return { data };
      },
    }),
    updateInstalledManagedBuild: builder.mutation<
      void,
      {
        managedId: string;
        settings: {
          enableCashManagement: boolean;
          requireEmployeePin: boolean;
          paymentSettings: PaymentSettings;
        };
      }
    >({
      async queryFn(query) {
        const data = await functions.updateInstalledManagedBuild(query);
        return { data };
      },
    }),
    fetchTheBuilds: builder.query<TBuild[], { companyId: string; gbType?: GlobalBlockTypes }>({
      async queryFn(config) {
        const data = await functions.fetchTheBuilds(config.companyId, config.gbType);
        return { data };
      },
      providesTags: () => [{ type: 'Builds', id: 'fetchTheBuilds' }],
    }),
    fetchManagedBuilds: builder.query<TBuild[], { gbType?: GlobalBlockTypes }>({
      async queryFn(config: { gbType?: GlobalBlockTypes }) {
        const data = await functions.fetchManagedBuilds(config);
        return { data };
      },
    }),
    fetchThemesBuilds: builder.query<
      TBuild[] | undefined,
      { currentUserType: string; currentUserAccountId?: string; tag?: string[] }
    >({
      async queryFn(params) {
        const data = await functions.fetchThemesBuilds(params);
        return { data };
      },
      providesTags: () => [{ type: 'Builds', id: 'fetchThemesBuilds' }],
    }),
  }),
});

export const {
  useFindOneBuildByCompanyQuery,
  useFetchBuildsByCompanyQuery,
  useFetchManagedReferencesByCompanyQuery,
  useFetchManagedBuildsByCompanyQuery,
  useFetchPublicManagedBuildsQuery,
  useInsertOneBuildMutation,
  useDeleteBuildMutation,
  useUninstallManagedBuildMutation,
  useDuplicateBuildMutation,
  useUpdateBuildSettingsMutation,
  useInstallManagedBuildMutation,
  useUpdateInstalledManagedBuildMutation,
  useFetchTheBuildsQuery,
  useFetchManagedBuildsQuery,
  useFetchThemesBuildsQuery,
} = buildApiService;
export default buildApiService;
